import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Lock, Mail, ArrowRight, ShieldCheck } from "lucide-react";

const Login = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) {
    return <Navigate to="/" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090b] p-6 relative overflow-hidden">
      {/* Abstract ambient glow */}
      <div className="pointer-events-none absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-zinc-800/20 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-zinc-700/10 blur-[120px]" />

      <div className="w-full max-w-sm fade-up relative z-10">
        <div className="flex flex-col items-center text-center mb-10">
          <div className="w-14 h-14 rounded-2xl bg-[#0e0e10] border border-zinc-800 flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(0,0,0,0.6)]">
            <ShieldCheck className="w-6 h-6 text-zinc-300" strokeWidth={1.5} />
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-600 font-bold mb-3">
            Página privada
          </div>
          <h1 className="font-display text-3xl font-black text-zinc-100 tracking-tighter">
            Sistema interno
          </h1>
          <p className="text-zinc-600 mt-3 text-sm">Acesso restrito. Use suas credenciais.</p>
        </div>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2">E-mail</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" strokeWidth={1.5} />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                required
                autoComplete="username"
                className="w-full pl-10 pr-3 py-3 bg-[#0e0e10] border border-zinc-800 rounded-lg text-zinc-100 placeholder-zinc-600 focus:border-zinc-500 focus:ring-1 focus:ring-zinc-500 focus:outline-none transition-all"
                placeholder="seu@email.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2">Senha</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" strokeWidth={1.5} />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                required
                autoComplete="current-password"
                className="w-full pl-10 pr-3 py-3 bg-[#0e0e10] border border-zinc-800 rounded-lg text-zinc-100 placeholder-zinc-600 focus:border-zinc-500 focus:ring-1 focus:ring-zinc-500 focus:outline-none transition-all"
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-400 bg-red-950/30 border border-red-900/50 rounded-lg px-4 py-2.5" data-testid="login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full flex items-center justify-center gap-2 py-3.5 bg-zinc-100 hover:bg-white text-zinc-900 font-bold rounded-lg shadow-[0_0_25px_rgba(255,255,255,0.08)] transition-all disabled:opacity-60"
          >
            {loading ? "Entrando..." : (<>Entrar <ArrowRight className="w-4 h-4" strokeWidth={2} /></>)}
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-zinc-900 text-center text-[11px] text-zinc-700">
          Acesso monitorado · Área restrita
        </div>
      </div>
    </div>
  );
};

export default Login;
